// ============================================================================
// COHOST INVITES (property_cohosts + cohost_invites)
// ============================================================================

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { PropertyAccessLevel } from "@/hooks/useMemberInvites";

export interface CohostInviteRow {
  id: string;
  property_id: string;
  invited_email: string;
  access_level: PropertyAccessLevel;
  permissions?: unknown;
  invited_by: string | null;
  token: string;
  accepted_at: string | null;
  created_at: string;
}

export interface CohostMemberRow {
  id: string;
  property_id: string;
  cohost_user_id: string;
  access_level: PropertyAccessLevel;
  permissions?: unknown;
  invited_by: string | null;
  accepted_at: string | null;
  created_at: string;
  display_name: string | null;
  grantee_email: string | null;
  first_name: string | null;
  last_name: string | null;
  team_name: string | null;
  role: string | null;
  avatar_url?: string | null;
}

export interface CreateCohostInviteResult {
  id: string;
  property_id: string;
  invited_email: string;
  access_level: PropertyAccessLevel;
  permissions?: unknown;
  token: string;
  created_at: string;
}

export type CohostInviteStatus = "accepted" | "pending";

export function cohostInviteStatus(inv: { accepted_at: string | null }): CohostInviteStatus {
  return inv.accepted_at ? "accepted" : "pending";
}

export const usePropertyCohostAccess = (propertyId: string | undefined) => {
  return useQuery({
    queryKey: ["property-cohost-access", propertyId],
    queryFn: async (): Promise<{ invites: CohostInviteRow[]; members: CohostMemberRow[] }> => {
      if (!propertyId) return { invites: [], members: [] };

      const cohortClient = supabase as unknown as {
        from: (table: string) => ReturnType<typeof supabase.from>;
      };

      const [invitesRes, membersRes] = await Promise.all([
        cohortClient
          .from("cohost_invites")
          .select("id, property_id, invited_email, access_level, permissions, invited_by, token, accepted_at, created_at")
          .eq("property_id", propertyId)
          .order("created_at", { ascending: false }),
        supabase.rpc("get_property_cohost_members", { p_property_id: propertyId }),
      ]);

      if (invitesRes.error) throw invitesRes.error;
      if (membersRes.error) throw membersRes.error;

      const invites = (invitesRes.data ?? []) as CohostInviteRow[];
      const rawMembers = (Array.isArray(membersRes.data) ? membersRes.data : []) as Array<{
        id: string;
        property_id: string;
        cohost_user_id: string;
        access_level: string;
        permissions?: unknown;
        invited_by: string | null;
        accepted_at: string | null;
        created_at: string;
        display_name?: string | null;
        first_name?: string | null;
        last_name?: string | null;
        team_name?: string | null;
        role?: string | null;
        grantee_email?: string | null;
        avatar_url?: string | null;
      }>;

      const members: CohostMemberRow[] = rawMembers.map((m) => ({
        id: m.id,
        property_id: m.property_id,
        cohost_user_id: m.cohost_user_id,
        access_level: (m.access_level === "full" ? "full" : "limited") as PropertyAccessLevel,
        permissions: m.permissions ?? null,
        invited_by: m.invited_by,
        accepted_at: m.accepted_at,
        created_at: m.created_at,
        display_name: m.display_name ?? null,
        grantee_email: m.grantee_email ?? null,
        first_name: m.first_name ?? null,
        last_name: m.last_name ?? null,
        team_name: m.team_name ?? null,
        role: m.role ?? null,
        avatar_url: m.avatar_url?.trim() || null,
      }));

      return { invites, members };
    },
    enabled: !!propertyId,
  });
};

export const useCreateCohostInvite = () => {
  const queryClient = useQueryClient();
  return useMutation<
    CreateCohostInviteResult,
    Error,
    { propertyId: string; email: string; accessLevel: PropertyAccessLevel; permissions: string[] }
  >({
    mutationFn: async ({ propertyId, email, accessLevel, permissions }) => {
      const { data, error } = await supabase.rpc("create_cohost_invite", {
        p_property_id: propertyId,
        p_email: email.trim().toLowerCase(),
        p_access_level: accessLevel,
        p_permissions: permissions,
      });
      if (error) throw new Error(error.message);
      if (!data) throw new Error("No data returned");
      return data as CreateCohostInviteResult;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["property-cohost-access", variables.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["cohost-properties"] });
    },
  });
};

export async function updateCohostInviteAccess(inviteId: string, accessLevel: PropertyAccessLevel): Promise<void> {
  const cohortClient = supabase as unknown as {
    from: (table: string) => ReturnType<typeof supabase.from>;
  };
  const { error } = await cohortClient
    .from("cohost_invites")
    .update({ access_level: accessLevel })
    .eq("id", inviteId)
    .is("accepted_at", null);
  if (error) throw new Error(error.message);
}

export async function updateCohostMemberPermissions(
  cohostRowId: string,
  permissions: string[]
): Promise<void> {
  const { error } = await supabase.rpc("update_cohost_member_permissions", {
    p_cohost_row_id: cohostRowId,
    p_permissions: permissions,
  });
  if (error) throw new Error(error.message);
}

export async function updateCohostInvitePermissions(
  inviteId: string,
  permissions: string[]
): Promise<void> {
  const { error } = await supabase.rpc("update_cohost_invite_permissions", {
    p_invite_id: inviteId,
    p_permissions: permissions,
  });
  if (error) throw new Error(error.message);
}

export async function updateCohostMemberAccessLevel(
  cohostRowId: string,
  accessLevel: PropertyAccessLevel
): Promise<void> {
  const cohortClient = supabase as unknown as {
    from: (table: string) => ReturnType<typeof supabase.from>;
  };
  const { error } = await cohortClient
    .from("property_cohosts")
    .update({ access_level: accessLevel })
    .eq("id", cohostRowId);
  if (error) throw new Error(error.message);
}

export async function deleteCohostInvite(inviteId: string): Promise<void> {
  const cohortClient = supabase as unknown as {
    from: (table: string) => ReturnType<typeof supabase.from>;
  };
  const { error } = await cohortClient.from("cohost_invites").delete().eq("id", inviteId);
  if (error) throw new Error(error.message);
}

export async function removeCohostAccess(
  propertyId: string,
  email: string,
  cohostUserId?: string | null
): Promise<void> {
  const { data, error } = await (supabase as unknown as {
    rpc: (
      fn: string,
      args: Record<string, unknown>
    ) => Promise<{ data: unknown; error: { message: string } | null }>;
  }).rpc("remove_team_member_access", {
    p_property_id: propertyId,
    p_target_email: email.trim() || null,
    p_cohost_user_id: cohostUserId ?? null,
  });
  if (error) throw new Error(error.message);
  const result = data as { ok?: boolean; message?: string } | null;
  if (!result?.ok) {
    throw new Error(result?.message || "Could not remove access");
  }
}
